// src/pages/EntryGate.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
    fetchJourney,
    JOURNEY_API_STORAGE_KEY,
    JOURNEY_TOKEN_STORAGE_KEY,
    setCurrentJourneyToken,
} from "../services/reservationService";
import { clearGuestData } from "../utils/storage";
import { useReservationContext } from "../context/ReservationContext";
import LoadingSpinner from "../components/LoadingSpinner";
import { CHECKIN_LANGUAGE_STORAGE_KEY, normalizeCheckinLanguage } from "../i18n/checkin";
import { setStoredAppLanguage } from "../hooks/useAppLanguage";
import {
    isRoomAccessEstablished,
    redactRoomPinsUnlessAllowed,
    storeRoomAccess,
} from "../utils/roomAccess";
import { storeJourneyAccess } from "../utils/journeyAccess";
import {
    capturePaymentReturnIntent,
    getJourneyReservationCode,
    inferPaymentFlow,
    PAYMENT_FLOW_DEPARTURE,
    readPaymentResume,
    savePaymentResume,
} from "../utils/paymentResume";
import {
    clearCompletedCheckinState,
    isOnlineCheckinComplete,
} from "../utils/checkinCompletion";

const getFirstRoom = (reservation) => reservation?.rooms?.[0] || null;

const getPrimaryGuest = (reservation) => {
    const roomGuest = reservation?.rooms?.flatMap((room) => room.guests || [])?.[0];
    return roomGuest || reservation?.booker || {};
};

const boolValue = (source, snakeKey, camelKey = snakeKey) =>
    source?.[snakeKey] === true || source?.[camelKey] === true;

export const shouldOpenRoomApp = (data) => {
    const onlineCheckin = data?.online_checkin || data?.onlineCheckin || {};
    const checkout = data?.checkout || {};
    const roomAccess = data?.room_access || data?.roomAccess || {};
    const accessScope = String(data?.access_scope || data?.accessScope || "").toUpperCase();

    if (accessScope === "ROOM_GUEST") {
        return true;
    }

    return (
        isOnlineCheckinComplete(onlineCheckin) ||
        isRoomAccessEstablished(roomAccess) ||
        boolValue(checkout, "should_redirect_to_app", "shouldRedirectToApp")
    );
};

const storeJourneyContext = (guestAccessToken, data) => {
    const reservation = data?.reservation || {};
    const roomAccess = storeRoomAccess(data?.room_access || data?.roomAccess);
    const rooms = redactRoomPinsUnlessAllowed(reservation.rooms || [], roomAccess);
    const safeReservation = {
        ...reservation,
        rooms,
    };
    const firstRoom = getFirstRoom(safeReservation);
    const primaryGuest = getPrimaryGuest(safeReservation);
    const language = normalizeCheckinLanguage(data?.language || primaryGuest?.language);
    storeJourneyAccess(data);

    localStorage.setItem(JOURNEY_TOKEN_STORAGE_KEY, guestAccessToken);
    localStorage.setItem("reservation-code", data?.reservation_id || reservation.reservation_number || "");
    localStorage.setItem("apaleo-property-id", data?.property_id || "");
    localStorage.setItem("hotel-name", data?.property_id || "");
    localStorage.setItem("rooms", JSON.stringify(rooms));
    localStorage.setItem("booker", JSON.stringify(reservation.booker || primaryGuest || {}));
    localStorage.setItem("guest-access", JSON.stringify(data?.guest_access || {}));
    localStorage.setItem("online-checkin", JSON.stringify(data?.online_checkin || {}));
    localStorage.setItem("checkout", JSON.stringify(data?.checkout || {}));
    localStorage.setItem(CHECKIN_LANGUAGE_STORAGE_KEY, language);

    if (data?.api) {
        localStorage.setItem(JOURNEY_API_STORAGE_KEY, JSON.stringify(data.api));
    }

    if (primaryGuest?.first_name) {
        localStorage.setItem("guest-firstname", primaryGuest.first_name);
    }
    if (primaryGuest?.last_name) {
        localStorage.setItem("guest-lastname", primaryGuest.last_name);
    }
    if (primaryGuest?.email) {
        localStorage.setItem("email", primaryGuest.email);
    }
    if (primaryGuest?.phone || primaryGuest?.mobile) {
        localStorage.setItem("telephone", primaryGuest.phone || primaryGuest.mobile);
    }

    if (firstRoom) {
        if (firstRoom.name || firstRoom.room_number) {
            localStorage.setItem("room-number", firstRoom.name || firstRoom.room_number);
        }
        localStorage.removeItem("pin-code");
        if (firstRoom.arrival) {
            localStorage.setItem("arrival", firstRoom.arrival);
        }
        if (firstRoom.departure) {
            localStorage.setItem("departure", firstRoom.departure);
        }
    }
    localStorage.removeItem("is_valid_in_pms");

    setStoredAppLanguage(language);
    window.dispatchEvent(new Event("booking-update"));
};

const clearJourneyContext = () => {
    clearGuestData();
    setCurrentJourneyToken("");
    localStorage.removeItem(JOURNEY_TOKEN_STORAGE_KEY);
    localStorage.removeItem(JOURNEY_API_STORAGE_KEY);
    window.dispatchEvent(new Event("booking-update"));
};

const ExpiredJourneyLink = () => (
    <main className="entry-gate expired-link-screen">
        <section className="expired-link-card">
            <p className="expired-link-kicker">Guest Journey</p>
            <h1>This guest journey link has expired.</h1>
            <p>
                Your stay has already ended. Please contact the hotel if you need help with your invoice or stay details.
            </p>
        </section>
    </main>
);

const BlockedJourneyLink = ({ message }) => (
    <main className="entry-gate expired-link-screen">
        <section className="expired-link-card">
            <p className="expired-link-kicker">Guest Journey</p>
            <h1>Room app not available yet.</h1>
            <p>{message}</p>
        </section>
    </main>
);

const EntryGate = () => {
    const { setHasFetchedReservation } = useReservationContext();
    const { guestAccessToken } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [linkExpired, setLinkExpired] = useState(false);
    const [blockedMessage, setBlockedMessage] = useState("");
    const paymentReturnIntent = useMemo(capturePaymentReturnIntent, [location.search]);

    useEffect(() => {
        let isMounted = true;

        if (!guestAccessToken) {
            navigate("/access-denied", { replace: true });
            return undefined;
        }

        setLinkExpired(false);
        setBlockedMessage("");
        setCurrentJourneyToken(guestAccessToken);

        const storedToken = localStorage.getItem(JOURNEY_TOKEN_STORAGE_KEY);
        if (storedToken !== guestAccessToken) {
            clearJourneyContext();
        }

        fetchJourney(guestAccessToken)
            .then((data) => {
                if (!isMounted) {
                    return;
                }

                storeJourneyContext(guestAccessToken, data);
                setHasFetchedReservation(true);

                const reservationCode = getJourneyReservationCode(data);
                const existingContext = paymentReturnIntent ? readPaymentResume(reservationCode) : null;
                const paymentFlow = paymentReturnIntent
                    ? existingContext?.flow || inferPaymentFlow(data)
                    : null;

                if (paymentReturnIntent && paymentFlow === PAYMENT_FLOW_DEPARTURE) {
                    savePaymentResume(reservationCode, {
                        ...existingContext,
                        flow: paymentFlow,
                        step: existingContext?.step || "choice",
                        pending: true,
                        settled: false,
                        returnedFromProvider: true,
                    });
                    navigate("/checkout", { replace: true });
                    return;
                }

                if (
                    isOnlineCheckinComplete(data?.online_checkin || data?.onlineCheckin) ||
                    isRoomAccessEstablished(data?.room_access || data?.roomAccess)
                ) {
                    clearCompletedCheckinState(reservationCode);
                    navigate("/home", { replace: true });
                    return;
                }

                if (paymentReturnIntent) {
                    savePaymentResume(reservationCode, {
                        ...existingContext,
                        flow: paymentFlow,
                        step: existingContext?.step || "payment",
                        pending: true,
                        settled: false,
                        returnedFromProvider: true,
                    });
                    navigate("/self-checkin", { replace: true });
                    return;
                }

                navigate(shouldOpenRoomApp(data) ? "/home" : "/self-checkin", { replace: true });
            })
            .catch((err) => {
                if (!isMounted) {
                    return;
                }
                if (err?.response?.status === 410) {
                    clearJourneyContext();
                    setHasFetchedReservation(false);
                    setLinkExpired(true);
                    return;
                }
                if (err?.response?.status === 403) {
                    const data = err.response?.data || {};
                    if (String(data.access_scope || data.accessScope || "").toUpperCase() === "ROOM_GUEST") {
                        setBlockedMessage(data.detail || "Online check-in must be completed by the booking owner before the room app is available.");
                        return;
                    }
                }
                console.error("Error during secure journey entry", err);
                navigate("/access-denied", { replace: true });
            });

        return () => {
            isMounted = false;
        };
    }, [guestAccessToken, navigate, paymentReturnIntent, setHasFetchedReservation]);

    if (linkExpired) {
        return <ExpiredJourneyLink />;
    }

    if (blockedMessage) {
        return <BlockedJourneyLink message={blockedMessage} />;
    }

    return (
        <div className="entry-gate">
            <div className="my-2">Loading reservation...</div>
            <LoadingSpinner />
        </div>
    )
};

export default EntryGate;
